import { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import logo from "../../assets/images/ecom_tamil_logo.png";
import { Lock, Eye, EyeOff, ArrowLeft, ArrowRight, CheckCircle2, KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "react-toastify";
import { postRequest } from "../../utils/networking";
import { apiUrls } from "../../utils/url";
import CommonToastContainer from "../../components/CommonToastContainer";

export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  
  const email = location.state?.email || "";
  const userid = location.state?.userid || "";
  const otp = location.state?.otp || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const showToast = (message, type = "success") => {
    if (type === "success") toast.success(message);
    else if (type === "error") toast.error(message);
    else if (type === "info") toast.info(message);
    else if (type === "warning") toast.warning(message);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!password) {
      showToast("Please enter a new password", "warning");
      return;
    }
    if (password.length < 6) {
      showToast("Password must be at least 6 characters", "warning");
      return;
    }
    if (password !== confirmPassword) {
      showToast("Passwords do not match", "error");
      return;
    }

    setIsLoading(true);
    try {
      const data = await postRequest(apiUrls.resetpassword, {
        userid: Number(userid || 270),
        password: password
      });
      console.log("Reset Password Response:", data);

      if (data.status === "S" || data.success) {
        showToast(data.message || "Password has been reset successfully! Please login.", "success");
        setTimeout(() => {
          navigate("/login", { state: { email } });
        }, 1200);
      } else {
        showToast(data.message || "Failed to reset password. Please try again.", "error");
      }
    } catch (error) {
      console.error("Reset Password Error:", error);
      showToast(error.message || "Failed to reset password. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-[#FFFDF9] relative overflow-hidden flex flex-col items-center justify-center p-4 sm:p-6 py-10 selection:bg-orange-100 selection:text-orange-900">
        
        {/* Soft Background Warm Blur Elements */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10">
          <div className="absolute top-0 right-0 w-[450px] h-[450px] bg-orange-200/30 rounded-full blur-[100px] -translate-y-1/3 translate-x-1/4" />
          <div className="absolute top-1/3 left-0 w-[400px] h-[400px] bg-amber-100/40 rounded-full blur-[90px] -translate-x-1/3" />
          <div className="absolute bottom-0 right-1/4 w-[450px] h-[450px] bg-orange-100/35 rounded-full blur-[100px] translate-y-1/4" />
        </div>

        {/* Main Card */}
        <div className="w-full max-w-[440px] bg-white rounded-3xl border border-orange-100/70 shadow-[0_12px_45px_-12px_rgba(234,88,12,0.12)] overflow-hidden transition-all duration-300">
          
          {/* Card Top Bar */}
          <div className="p-6 pb-2 sm:p-7 sm:pb-3 flex items-center justify-between">
            <div 
              onClick={() => navigate("/")} 
              className="flex items-center gap-2 cursor-pointer group"
            >
              <img
                src={logo}
                alt="Ecom Tamil"
                className="h-10 w-auto object-contain transition-transform group-hover:scale-105"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = "none";
                }}
              />
            </div>

            <Link
              to="/login"
              state={{ email }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 hover:bg-slate-100 border border-slate-200/80 text-[11px] font-medium text-slate-600 transition active:scale-95 cursor-pointer"
            >
              <ArrowLeft size={12} />
              <span>Back to Login</span>
            </Link>
          </div>

          {/* Form Content Area */}
          <div className="p-6 pt-3 sm:p-7 sm:pt-4">
            
            {/* Heading Section */}
            <div className="mb-5">
              <div className="inline-flex items-center gap-1.5 text-xs font-bold text-orange-600 mb-1.5">
                <KeyRound size={15} className="text-orange-500" />
                <span>Security</span>
              </div>
              <h1 className="text-2xl sm:text-[26px] font-black text-slate-800 tracking-tight leading-tight mb-1">
                Create new password
              </h1>
              <p className="text-xs text-slate-500 leading-relaxed">
                Your new password must be different from previous used passwords.
              </p>
            </div>

            {/* Form Fields */}
            <form onSubmit={handleResetPassword} className="space-y-3.5">
              
              {/* New Password */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-slate-600 pl-0.5">
                  New Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <Lock size={16} />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="w-full pl-10 pr-10 py-3 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition cursor-pointer"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-slate-600 pl-0.5">
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <Lock size={16} />
                  </div>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="w-full pl-10 pr-10 py-3 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition cursor-pointer"
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Password Requirements Checklist */}
              <div className="space-y-1 py-1.5 text-[11px]">
                <div className={`flex items-center gap-1.5 transition-colors ${password.length >= 6 ? 'text-emerald-600 font-semibold' : 'text-slate-400'}`}>
                  <CheckCircle2 size={13} className={password.length >= 6 ? 'text-emerald-500' : 'text-slate-300'} />
                  <span>At least 6 characters</span>
                </div>
                <div className={`flex items-center gap-1.5 transition-colors ${/\d/.test(password) ? 'text-emerald-600 font-semibold' : 'text-slate-400'}`}>
                  <CheckCircle2 size={13} className={/\d/.test(password) ? 'text-emerald-500' : 'text-slate-300'} />
                  <span>Include a number</span>
                </div>
                <div className={`flex items-center gap-1.5 transition-colors ${password && confirmPassword && password === confirmPassword ? 'text-emerald-600 font-semibold' : 'text-slate-400'}`}>
                  <CheckCircle2 size={13} className={password && confirmPassword && password === confirmPassword ? 'text-emerald-500' : 'text-slate-300'} />
                  <span>Passwords match</span>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isLoading || password.length < 6 || password !== confirmPassword}
                  className={`w-full py-3.5 px-4 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 shadow-sm cursor-pointer ${
                    isLoading || password.length < 6 || password !== confirmPassword
                      ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                      : "bg-orange-600 hover:bg-orange-700 text-white shadow-orange-500/20 hover:shadow-md active:scale-[0.99]"
                  }`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Updating Password...</span>
                    </>
                  ) : (
                    <>
                      <span>Reset Password</span>
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </div>
            </form>

          </div>

          {/* Footer note */}
          <div className="px-6 py-3.5 bg-slate-50/70 border-t border-slate-100/80 text-center">
            <p className="text-[11px] text-slate-400 flex items-center justify-center gap-1.5">
              <ShieldCheck size={12} className="text-orange-500" />
              <span>Password will be securely encrypted</span>
            </p>
          </div>

        </div>
      </div>
      <CommonToastContainer />
    </>
  );
}
